import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Smartphone } from 'lucide-react'
import { addToHomeScreen, retrieveLaunchParams } from '@tma.js/sdk'

/**
 * Prompts the user to pin the mini-app to their phone's home screen via
 * the native TG dialog (Mini Apps v8.0+). Silently hides when the current
 * TG client doesn't support the feature (older versions, MAX, web TG).
 *
 * iOS caveat: even when addToHomeScreen.isAvailable() returns true, the
 * iOS TG client sometimes doesn't show the system prompt (Apple hasn't
 * standardised the OS-level "add to home screen" hook for third-party
 * webviews yet). We surface a clearer "если не сработало — добавь
 * вручную через 'Поделиться → На экран Домой' в Safari" hint after
 * clicking so iOS users aren't left wondering why nothing happened.
 */
function detectIos(): boolean {
  try {
    const params = retrieveLaunchParams()
    const platform = (params as { tgWebAppPlatform?: string }).tgWebAppPlatform
    return platform === 'ios' || platform === 'ipados'
  } catch {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent)
  }
}

export function HomeScreenButton() {
  const { t } = useTranslation()
  const [requested, setRequested] = useState(false)
  const isIos = detectIos()

  if (!addToHomeScreen.isAvailable()) return null

  return (
    <button
      type="button"
      onClick={() => {
        try {
          addToHomeScreen()
          setRequested(true)
          if (isIos) {
            // The native iOS prompt doesn't always appear; tell the user
            // upfront how to recover manually so they don't try 5 times.
            toast(t('profile.home_screen_prompted_ios'), { icon: 'ℹ️', duration: 6000 })
          } else {
            toast.success(t('profile.home_screen_prompted'))
          }
        } catch (err) {
          toast.error((err as Error).message)
        }
      }}
      className="flex w-full items-center gap-3 rounded-2xl bg-white/70 p-3 text-left ring-1 ring-sage-200/60 transition hover:bg-white active:scale-[0.99]"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fbeadd] text-copper">
        <Smartphone className="h-5 w-5" strokeWidth={1.6} aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-sm text-sage-800 leading-snug">
          {t('profile.home_screen_title')}
        </p>
        <p className="mt-0.5 text-xs text-sage-500">
          {requested ? t('profile.home_screen_prompted_hint') : t('profile.home_screen_desc')}
        </p>
      </div>
    </button>
  )
}
